import React, { useState, useEffect, useRef } from 'react';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import '../styles/StateManagement.css';
import {
  Box,
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Grid,
  Autocomplete,
  Tabs,
  Tab,
  LinearProgress,
  CircularProgress } from
'@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import RemoveIcon from '@mui/icons-material/Remove';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import api from '../services/api';
import SearchableDataTable from '../components/SearchableDataTable';
import { usePointInTimeFilter, filterDataByPointInTime } from '../hooks/usePointInTimeFilter';
import { useStateFilter } from '../components/layout/Layout';

// US States list with abbreviations and full names
const US_STATES = [
{ code: 'AL', name: 'Alabama' },
{ code: 'AK', name: 'Alaska' },
{ code: 'AZ', name: 'Arizona' },
{ code: 'AR', name: 'Arkansas' },
{ code: 'CA', name: 'California' },
{ code: 'CO', name: 'Colorado' },
{ code: 'CT', name: 'Connecticut' },
{ code: 'DE', name: 'Delaware' },
{ code: 'FL', name: 'Florida' },
{ code: 'GA', name: 'Georgia' },
{ code: 'HI', name: 'Hawaii' },
{ code: 'ID', name: 'Idaho' },
{ code: 'IL', name: 'Illinois' },
{ code: 'IN', name: 'Indiana' },
{ code: 'IA', name: 'Iowa' },
{ code: 'KS', name: 'Kansas' },
{ code: 'KY', name: 'Kentucky' },
{ code: 'LA', name: 'Louisiana' },
{ code: 'ME', name: 'Maine' },
{ code: 'MD', name: 'Maryland' },
{ code: 'MA', name: 'Massachusetts' },
{ code: 'MI', name: 'Michigan' },
{ code: 'MN', name: 'Minnesota' },
{ code: 'MS', name: 'Mississippi' },
{ code: 'MO', name: 'Missouri' },
{ code: 'MT', name: 'Montana' },
{ code: 'NE', name: 'Nebraska' },
{ code: 'NV', name: 'Nevada' },
{ code: 'NH', name: 'New Hampshire' },
{ code: 'NJ', name: 'New Jersey' },
{ code: 'NM', name: 'New Mexico' },
{ code: 'NY', name: 'New York' },
{ code: 'NC', name: 'North Carolina' },
{ code: 'ND', name: 'North Dakota' },
{ code: 'OH', name: 'Ohio' },
{ code: 'OK', name: 'Oklahoma' },
{ code: 'OR', name: 'Oregon' },
{ code: 'PA', name: 'Pennsylvania' },
{ code: 'RI', name: 'Rhode Island' },
{ code: 'SC', name: 'South Carolina' },
{ code: 'SD', name: 'South Dakota' },
{ code: 'TN', name: 'Tennessee' },
{ code: 'TX', name: 'Texas' },
{ code: 'UT', name: 'Utah' },
{ code: 'VT', name: 'Vermont' },
{ code: 'VA', name: 'Virginia' },
{ code: 'WA', name: 'Washington' },
{ code: 'WV', name: 'West Virginia' },
{ code: 'WI', name: 'Wisconsin' },
{ code: 'WY', name: 'Wyoming' }];


const StateManagement = () => {
  // State for states
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(false);
  // Get PointInTime filter from context
  const { selectedPointInTime } = usePointInTimeFilter();
  // Get State filter from context
  const { selectedState } = useStateFilter();

  // State for dialogs
  const [openDialog, setOpenDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // Utility to generate FilingCode
  const generateFilingCode = ({ FilingType, State, EntityCode }) => {
    if (!FilingType || !State || !EntityCode) return '';
    switch (FilingType) {
      case 'Separate':
        return `SS_${State}_${EntityCode}`;
      case 'Unitary/Combined':
        return `COMB_${State}_${EntityCode}`;
      case 'Nexus':
        return `NEX_${State}_${EntityCode}`;
      default:
        return '';
    }
  };

  const [currentState, setCurrentState] = useState({
    EntityId: '',
    FilingType: '',
    TaxType: '',
    State: '',
    FilingName: '',
    PointInTime: '',
    ModifiedBy: 'admin',
    FilingCode: '',
    entityList: []
  });

  // State for notifications
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Point In Time management state
  const [pointInTimes, setPointInTimes] = useState([]);
  const [loadingPointInTimes, setLoadingPointInTimes] = useState(false);

  // Import functionality
  const fileInputRef = useRef(null);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // State for row selection
  const [selectedRows, setSelectedRows] = useState([]);

  // Master entities for dropdown
  const [masterEntities, setMasterEntities] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);

  // Entities for Associated Entities section
  const [entities, setEntities] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Fetch entities for Associated Entities section
  const fetchEntities = async () => {
    setLoadingEntities(true);
    try {
      const response = await api.get('/entity-models/master');
      setEntities(response.data.data || []);
    } catch (error) {
      console.error('Error fetching entities:', error);
      setNotification({
        open: true,
        message: 'Failed to fetch entities',
        severity: 'error'
      });
    } finally {
      setLoadingEntities(false);
    }
  };

  // Fetch master entities
  const fetchMasterEntities = async () => {
    setEntityLoading(true);
    try {
      const response = await api.get('/entity-models/master');
      if (response.data && response.data.data) {
        setMasterEntities(response.data.data);
      }
    } catch (error) {
      setNotification({ open: true, message: 'Failed to load master entities', severity: 'error' });
    } finally {
      setEntityLoading(false);
    }
  };

  useEffect(() => {
    fetchMasterEntities();
  }, []);

  // Fetch states from API
  const fetchStates = async () => {
    setLoading(true);
    try {
      const [statesResponse, pointInTimesResponse] = await Promise.all([
      api.get('/states/master'),
      api.get('/point-in-times')]
      );

      // Create lookup maps for PointInTime codes to names and names to codes (case-insensitive)
      const codeToNameMap = new Map();
      const nameToCodeMap = new Map();
      (pointInTimesResponse.data.data || []).forEach((pit) => {
        codeToNameMap.set(pit.Code.toLowerCase(), pit.Name);
        nameToCodeMap.set(pit.Name.toLowerCase(), pit.Code);
        nameToCodeMap.set(pit.Code.toLowerCase(), pit.Code);
      });

      // Transform API data to match frontend schema
      const transformedStates = (statesResponse.data.data || []).map((state) => {
        // Determine if PointInTime field contains a code or a name
        const pitValue = state.PointInTime || '';
        const pitValueLower = pitValue.toLowerCase();
        const isCode = codeToNameMap.has(pitValueLower);
        const isName = !isCode && nameToCodeMap.has(pitValueLower);

        // Extract code and name appropriately
        let pitCode = '';
        let pitName = '';

        if (isCode) {
          pitCode = pitValue;
          pitName = codeToNameMap.get(pitValueLower) || pitValue;
        } else if (isName) {
          pitCode = nameToCodeMap.get(pitValueLower) || pitValue;
          pitName = pitValue;
        } else {
          // Unknown value - store as-is
          pitCode = pitValue;
          pitName = pitValue;
        }

        return {
          id: state.id,
          EntityCode: state.EntityCode,
          FilingType: state.FilingType,
          TaxType: state.TaxType,
          State: state.State,
          FilingName: state.FilingName || '',
          FilingCode: state.FilingCode || generateFilingCode({ FilingType: state.FilingType, State: state.State, EntityCode: state.EntityCode }),
          ModifiedBy: state.ModifiedBy,
          ModifiedOn: state.ModifiedOn,
          dateCreated: state.dateCreated,
          entityName: state.EntityCode || 'Unknown',
          PointInTimeCode: pitCode,
          PointInTime: pitName,
          entityList: state.entityList || []
        };
      });

      setStates(transformedStates);
    } catch (error) {
      console.error('Error fetching states:', error);
      setNotification({
        open: true,
        message: 'Error fetching states',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch Point In Times for the dropdown
  const fetchPointInTimes = async () => {
    setLoadingPointInTimes(true);
    try {
      const response = await api.get('/point-in-times');
      setPointInTimes(response.data.data || []);
    } catch (error) {
      console.error('Error fetching point in times:', error);
    } finally {
      setLoadingPointInTimes(false);
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchStates();
    fetchPointInTimes();
    fetchEntities();
  }, []);

  // Handle adding a new state
  const handleAddNew = () => {
    setEditMode(false);
    // Use default values for new states
    setCurrentState({
      EntityCode: '',
      FilingType: '',
      TaxType: '',
      State: '',
      FilingName: '',
      // Store code in both PointInTime and PointInTimeCode for consistency
      PointInTime: selectedPointInTime && selectedPointInTime !== 'All' ? selectedPointInTime : '',
      PointInTimeCode: selectedPointInTime && selectedPointInTime !== 'All' ? selectedPointInTime : '',
      ModifiedBy: 'admin',
      FilingCode: '',
      entityList: []
    });
    setSelectedEntity(null);
    fetchPointInTimes();
    fetchEntities();
    setOpenDialog(true);
  };

  // Handle editing a state
  const handleEdit = async (state) => {
    setEditMode(true);
    setCurrentState({
      ...state,
      entityList: state.entityList || []
    });
    setSelectedEntity(null);
    fetchPointInTimes();
    await fetchEntities();
    setOpenDialog(true);
  };

  // Handle selection change
  const handleSelectionChange = (newSelectionModel) => {

    setSelectedRows(newSelectionModel);
  };

  // Handle deleting selected states
  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) return;

    if (window.confirm(`Are you sure you want to delete ${selectedRows.length} selected states?`)) {
      setDeleting(true);
      try {
        // Delete multiple states
        const deletePromises = selectedRows.map((id) => api.delete(`/states/master/${id}`));
        await Promise.all(deletePromises);

        // Refresh states after successful deletion
        await fetchStates();
        setNotification({
          open: true,
          message: `Successfully deleted ${selectedRows.length} states`,
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting states:', error);
        setNotification({
          open: true,
          message: 'Failed to delete states',
          severity: 'error'
        });
      } finally {
        setDeleting(false);
      }
    }
  };

  // Handle deleting a single state
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this state?')) {
      try {
        await api.delete(`/states/master/${id}`);
        // Refresh states after successful deletion
        await fetchStates();
        setNotification({
          open: true,
          message: 'State deleted successfully',
          severity: 'success'
        });
      } catch (error) {
        console.error('Error deleting state:', error);
        setNotification({
          open: true,
          message: 'Failed to delete state',
          severity: 'error'
        });
      }
    }
  };

  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const handleRequestClose = () => setConfirmCloseOpen(true);

  // Handle closing the dialog
  const handleClose = () => {
    setOpenDialog(false);
    setConfirmCloseOpen(false);
  };

  // Handle saving a state
  const handleSave = async () => {
    // Validation: Check if FilingCode is unique per PointInTime
    const currentFilingCode = generateFilingCode({
      FilingType: currentState.FilingType,
      State: currentState.State,
      EntityCode: currentState.EntityCode
    });

    const duplicateFilingCode = states.find(
      (s) =>
      s.FilingCode === currentFilingCode &&
      s.PointInTimeCode === currentState.PointInTime && (
      !editMode || s.id !== currentState.id)
    );

    if (duplicateFilingCode) {
      setNotification({
        open: true,
        message:
        `Warning: A state filing with code "${currentFilingCode}" already exists for this point in time.\n\nFiling codes must be unique per point in time. Please modify the Filing Type, State, or Filing Entity to create a unique filing code.`,
        severity: 'warning'
      });
      return;
    }

    try {
      // Extract entity codes from entityList (handle both object and string formats)
      const entityCodes = (currentState.entityList || []).map((item) =>
      typeof item === 'object' ? item.EntityCode : item
      );

      const payload = {
        ...currentState,
        entityList: entityCodes,
        FilingCode: generateFilingCode({ FilingType: currentState.FilingType, State: currentState.State, EntityCode: currentState.EntityCode }),
        // Always send PointInTime as the code, not the name
        PointInTime: currentState.PointInTimeCode || currentState.PointInTime
      };









      if (editMode) {
        // Update existing state

        await api.put(`/states/master/${currentState.id}`, payload);
        // Refresh the states list from the API
        await fetchStates();
        setNotification({
          open: true,
          message: 'State updated successfully',
          severity: 'success'
        });
      } else {
        // Create new state

        await api.post('/states/master', payload);
        // Refresh the states list from the API
        await fetchStates();
        setNotification({
          open: true,
          message: 'State created successfully',
          severity: 'success'
        });
      }
      setOpenDialog(false);
    } catch (error) {
      console.error('Error saving state:', error);
      setNotification({
        open: true,
        message: 'Failed to save state',
        severity: 'error'
      });
    }
  };

  // Handle click on Import button
  const handleImportClick = () => {
    fileInputRef.current.click();
  };

  // Handle file selection for import - matches ScenarioManagement pattern
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setImporting(true);
      const reader = new FileReader();
      reader.onload = async (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        // Get first worksheet
        const worksheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[worksheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          throw new Error('The Excel file is empty');
        }

        // Fetch PointInTime master data to map names to codes
        const pointInTimesResponse = await api.get('/point-in-times');
        const pointInTimes = pointInTimesResponse.data.data || [];

        // Create a map from PointInTime Name to Code (case-insensitive)
        const pitNameToCodeMap = new Map();
        pointInTimes.forEach((pit) => {
          pitNameToCodeMap.set(pit.Name.toLowerCase().trim(), pit.Code);
          // Also map the code to itself in case Excel has codes
          pitNameToCodeMap.set(pit.Code.toLowerCase().trim(), pit.Code);
        });

        // Fetch master entities for validation
        const entitiesResponse = await api.get('/entity-models/master');
        const validEntityCodes = new Set(
          (entitiesResponse.data.data || []).map((e) => e.EntityCode.toUpperCase())
        );

        // Get all column names from the first row, case-insensitive
        const fileColumns = Object.keys(jsonData[0] || {});
        const fileColumnsLower = fileColumns.map((col) => col.toLowerCase());

        // Map frontend column names to possible Excel column names
        const columnMappings = {
          'EntityCode': ['filing entity code', 'filing entity', 'entity code', 'entitycode', 'filingentitycode'],
          'FilingType': ['filing type', 'filingtype', 'type'],
          'FilingName': ['filing name', 'filingname', 'name'],
          'TaxType': ['tax type', 'taxtype'],
          'State': ['state'],
          'PointInTime': ['point in time', 'pointintime', 'pit', 'period'],
          'ModifiedBy': ['modified by', 'modifiedby'],
          'EntityList': ['entity list', 'entitylist', 'associated entities', 'entities']
        };

        // Find matching columns in the Excel file
        const matchedColumns = {};
        let missingColumns = [];

        for (const [field, possibleNames] of Object.entries(columnMappings)) {
          const found = fileColumnsLower.some((col, index) => {
            if (possibleNames.includes(col.toLowerCase())) {
              matchedColumns[field] = fileColumns[index];
              return true;
            }
            return false;
          });

          if (!found && ['EntityCode', 'FilingType', 'TaxType', 'State', 'PointInTime'].includes(field)) {
            missingColumns.push(possibleNames[0]);
          }
        }

        if (missingColumns.length > 0) {
          throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
        }

        // Get all existing states to check for duplicates
        const existingStates = await api.get('/states/master');
        const statesMap = new Map(
          existingStates.data.data.map((s) => [`${s.EntityCode}-${s.State}-${s.PointInTime}`.toLowerCase(), s])
        );




        // Process each row and track results
        const importResults = {
          created: 0,
          updated: 0,
          errors: []
        };

        // Process rows sequentially to avoid race conditions
        for (let i = 0; i < jsonData.length; i++) {
          try {
            const row = jsonData[i];


            // Extract values using matched column names
            const entityCode = String(row[matchedColumns.EntityCode] || '').trim();
            const filingType = String(row[matchedColumns.FilingType] || '').trim();
            const taxType = String(row[matchedColumns.TaxType] || '').trim();
            const state = String(row[matchedColumns.State] || '').trim();
            const pointInTimeInput = String(row[matchedColumns.PointInTime] || '').trim();
            const filingName = String(row[matchedColumns.FilingName] || '').trim();



            // Validate required fields
            if (!entityCode) {
              throw new Error('Entity Code is required');
            }
            if (!filingType) {
              throw new Error('Filing Type is required');
            }
            if (!taxType) {
              throw new Error('Tax Type is required');
            }
            if (!state) {
              throw new Error('State is required');
            }
            if (!pointInTimeInput) {
              throw new Error('Point In Time is required');
            }

            // Validate EntityCode exists in master entities
            if (!validEntityCodes.has(entityCode.toUpperCase())) {
              throw new Error(`Invalid Entity Code: "${entityCode}". Entity does not exist in master entities.`);
            }

            // Validate dropdown field values
            const validFilingTypes = ['Separate', 'Unitary/Combined', 'Nexus'];
            const validTaxTypes = ['Income', 'Franchise', 'Minimum', 'Other'];

            // Case-insensitive validation for FilingType
            const filingTypeMatch = validFilingTypes.find(
              (valid) => valid.toLowerCase() === filingType.toLowerCase()
            );
            if (!filingTypeMatch) {
              throw new Error(`Invalid Filing Type: "${filingType}". Must be one of: ${validFilingTypes.join(', ')}`);
            }
            const normalizedFilingType = filingTypeMatch; // Use the properly cased version

            // Case-insensitive validation for TaxType
            const taxTypeMatch = validTaxTypes.find(
              (valid) => valid.toLowerCase() === taxType.toLowerCase()
            );
            if (!taxTypeMatch) {
              throw new Error(`Invalid Tax Type: "${taxType}". Must be one of: ${validTaxTypes.join(', ')}`);
            }
            const normalizedTaxType = taxTypeMatch; // Use the properly cased version

            // Map PointInTime Name to Code (case-insensitive lookup)
            const pointInTimeCode = pitNameToCodeMap.get(pointInTimeInput.toLowerCase().trim());

            if (!pointInTimeCode) {
              throw new Error(`Invalid Point In Time: "${pointInTimeInput}". Must match a valid PointInTime Name or Code.`);
            }

            // Generate FilingCode using the existing function
            const filingCode = generateFilingCode({ FilingType: normalizedFilingType, State: state, EntityCode: entityCode });

            // Parse Entity List (semicolon-separated; commas also tolerated)
            let entityList = [];
            if (matchedColumns.EntityList) {
              const rawList = String(row[matchedColumns.EntityList] || '').trim();
              if (rawList) {
                // Accept ; , | : or newline as separators between codes
                const parsedCodes = rawList.
                split(/[;,|:\r\n]+/).
                map((s) => s.trim()).
                filter(Boolean);

                const invalidCodes = parsedCodes.filter(
                  (code) => !validEntityCodes.has(code.toUpperCase())
                );
                if (invalidCodes.length > 0) {
                  throw new Error(
                    `Invalid Entity List code(s): "${invalidCodes.join(', ')}". Codes must exist in master entities.`
                  );
                }
                // De-duplicate while preserving order
                entityList = Array.from(new Set(parsedCodes));
              }
            }

            const stateData = {
              EntityCode: entityCode,
              FilingType: normalizedFilingType, // Use normalized value
              FilingName: filingName,
              TaxType: normalizedTaxType, // Use normalized value
              State: state,
              PointInTime: pointInTimeCode, // Store the CODE, not the name
              FilingCode: filingCode,
              entityList,
              ModifiedBy: row[matchedColumns.ModifiedBy] || 'admin',
              dateCreated: new Date().toISOString(),
              ModifiedOn: new Date().toISOString()
            };

            // Check if state with this combination already exists
            const stateKey = `${entityCode}-${state}-${pointInTimeCode}`.toLowerCase();
            const existingState = statesMap.get(stateKey);




            if (existingState) {
              // Update existing state

              await api.put(`/states/master/${existingState.id}`, stateData);
              importResults.updated++;
            } else {
              // Create new state

              try {
                const response = await api.post('/states/master', stateData);

                importResults.created++;
              } catch (apiError) {
                console.error('API Error details:', apiError.response?.data);
                throw apiError;
              }
            }

          } catch (error) {
            console.error(`Error processing row ${i + 1}:`, jsonData[i], error);
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            importResults.errors.push(`Row ${i + 1}: ${errorMessage}`);
          }
        }

        // Check if there were any errors
        if (importResults.errors.length > 0) {
          const errorMessage = importResults.errors.length === jsonData.length ?
          'All rows had errors. No data was imported.' :
          `Completed with ${importResults.errors.length} error(s) out of ${jsonData.length} rows.`;

          if (importResults.created === 0 && importResults.updated === 0) {
            throw new Error(`Import failed: ${errorMessage}\n\n${importResults.errors.join('\n')}`);
          } else {
            // Some rows succeeded, show warning
            setNotification({
              open: true,
              message: `Partial import: ${importResults.created} created, ${importResults.updated} updated, ${importResults.errors.length} errors`,
              severity: 'warning',
              autoHideDuration: 10000
            });
          }
        }

        // Refresh data
        await fetchStates();

        // Only show success message if no errors were shown before
        if (importResults.errors.length === 0) {
          const updatedMsg = importResults.updated > 0 ? `, updated ${importResults.updated}` : '';
          setNotification({
            open: true,
            message: `Successfully imported ${importResults.created} state${importResults.created !== 1 ? 's' : ''}${updatedMsg}`,
            severity: 'success',
            autoHideDuration: 5000
          });
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('Error importing states:', error);
      setImporting(false);
      setNotification({
        open: true,
        message: 'Failed to import states',
        severity: 'error'
      });
    }

    // Reset file input so the same file can be selected again
    event.target.value = null;
  };

  // Function to export states data to Excel
  const exportToExcel = () => {
    try {
      // Determine which states to export based on selection and current filters
      const statesToExport = selectedRows.length > 0 ?
      filteredStates.filter((state) => selectedRows.includes(state.id)) :
      filteredStates;

      // Define column headers
      const headers = ['Filing Entity', 'Filing Name', 'Filing Type', 'Tax Type', 'State', 'Point In Time', 'Filing Code', 'Entity List'];

      // Filter data to include only the columns we want to export
      const exportData = statesToExport.map((state) => {
        // entityList may contain strings or objects { EntityCode }
        const entityCodes = (state.entityList || []).map((item) =>
        typeof item === 'object' && item !== null ? item.EntityCode : item
        ).filter(Boolean);

        return {
          'Filing Entity': state.EntityCode,
          'Filing Name': state.FilingName || '',
          'Filing Type': state.FilingType,
          'Tax Type': state.TaxType,
          'State': state.State,
          'Point In Time': state.PointInTimeCode || state.PointInTime || '',
          'Filing Code': state.FilingCode,
          'Entity List': entityCodes.join('; ')
        };
      });

      // Create a worksheet from the exported data
      // Always include headers even if no data by specifying header option
      const worksheet = XLSX.utils.json_to_sheet(exportData, {
        header: headers
      });

      // Create a workbook and add the worksheet
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'States');

      // Generate Excel file
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Save the file
      saveAs(data, `States_Export_${new Date().toISOString().slice(0, 10)}.xlsx`);

      setNotification({
        open: true,
        message: 'States exported successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error exporting states:', error);
      setNotification({
        open: true,
        message: 'Failed to export states',
        severity: 'error'
      });
    }
  };

  // Handle notification close
  const handleNotificationClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setNotification({ ...notification, open: false });
  };

  // Define columns for the data grid
  const stateColumns = [
  {
    field: 'FilingCode',
    headerName: 'Filing Code',
    flex: 0.8,
    minWidth: 120,
    renderCell: (params) =>
    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000' }}>
          {params.value}
        </Typography>

  },
  {
    field: 'FilingName',
    headerName: 'Filing Name',
    flex: 0.8,
    minWidth: 120
  },
  {
    field: 'FilingType',
    headerName: 'Filing Type',
    flex: 0.8,
    minWidth: 120
  },
  {
    field: 'TaxType',
    headerName: 'Tax Type',
    flex: 0.8,
    minWidth: 120
  },
  {
    field: 'State',
    headerName: 'State',
    flex: 0.7,
    minWidth: 100
  },
  {
    field: 'PointInTime',
    headerName: 'Point In Time',
    flex: 0.7,
    minWidth: 110
  },
  {
    field: 'actions',
    headerName: 'Actions',
    flex: 0.5,
    minWidth: 100,
    renderCell: (params) =>
    <Box sx={{ display: 'flex' }}>
          <IconButton
        size="small"
        onClick={() => handleEdit(params.row)}
        title="Edit State">

            <img src="/assets/Icons/icon_pencil.svg" alt="Edit" style={{ width: 18, height: 18 }} />
          </IconButton>
          <IconButton
        size="small"
        onClick={() => handleDelete(params.row.id)}
        title="Delete State">

            <img src="/assets/Icons/icon_trash.svg" alt="Delete" style={{ width: 18, height: 18 }} />
          </IconButton>
        </Box>

  }];


  // Filter states by PointInTime and State
  const filteredStates = React.useMemo(() => {
    let result = states;
    if (selectedPointInTime && selectedPointInTime !== 'All') {
      result = result.filter((state) => state.PointInTimeCode === selectedPointInTime);
    }
    const stateArr = Array.isArray(selectedState) ? selectedState : [];
    if (stateArr.length > 0) {
      result = result.filter((state) => stateArr.includes(state.State));
    }
    return result;
  }, [states, selectedPointInTime, selectedState]);

  return (
    <Box className="state-management-container">
      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>

        <Alert onClose={handleNotificationClose} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Main Content */}
      <Box className="main-content">
        {/* Toolbar */}
        <Box className="toolbar-container">
          <Box className="selection-info" sx={{ pl: 0 }}>
            <Typography variant="h6" className="page-title" sx={{ pl: 0, fontWeight: 'bold' }}>Manage States</Typography>
            {selectedRows.length > 0 &&
            <Typography variant="body2" className="selection-info" sx={{ ml: 2 }}>
                {selectedRows.length} row{selectedRows.length !== 1 ? 's' : ''} selected
              </Typography>
            }
          </Box>
          <Box className="button-group">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              className="action-button"
              onClick={exportToExcel}
              disabled={states.length === 0}>

              Export
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadIcon />}
              className="action-button"
              onClick={handleImportClick}>

              Import
            </Button>
            {/* Hidden file input for Excel import */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              accept=".xlsx, .xls" />

            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddNew}
              className="action-button">

              Add
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <RemoveIcon />}
              onClick={handleDeleteSelected}
              disabled={selectedRows.length === 0 || deleting}
              className="action-button"
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.4
                }
              }}>

              {deleting ? 'Deleting...' : 'Remove'}
            </Button>
          </Box>
        </Box>

        {/* Data Table */}
        <Box className="table-container">
          {loading ?
          <LinearProgress /> :

          <SearchableDataTable
            searchPlaceholder="Search States..."
            className="custom-datagrid"
            rows={filteredStates}
            columns={stateColumns}
            // Pagination props removed as they're not needed in Data Grid Pro
            checkboxSelection
            disableSelectionOnClick
            onSelectionModelChange={handleSelectionChange}
            selectionModel={selectedRows}
            height="100%" />

          }
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <UnsavedChangesDialog
        open={confirmCloseOpen}
        onConfirm={handleClose}
        onCancel={() => setConfirmCloseOpen(false)} />

      <Dialog
        open={openDialog}
        onClose={handleRequestClose}
        maxWidth="md"
        fullWidth>

        <DialogTitle>{editMode ? 'Edit State' : 'Add New State'}</DialogTitle>
        <DialogContent className="dialog-content">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={masterEntities.
                filter((e) => {
                  // If no PointInTime selected (empty string means "All" was selected in layout), show all entities
                  if (!currentState.PointInTimeCode || currentState.PointInTimeCode === '') {
                    return true;
                  }
                  // Otherwise, filter by specific PointInTime
                  return e.PointInTime === currentState.PointInTimeCode;
                }).
                filter((e, index, self) => index === self.findIndex((x) => x.EntityCode === e.EntityCode)).
                sort((a, b) => a.EntityCode.localeCompare(b.EntityCode))}
                getOptionLabel={(option) => {
                  if (typeof option === 'string') {
                    const entity = masterEntities.find((e) => e.EntityCode === option);
                    return entity ? `${entity.EntityCode} - ${entity.EntityName}` : option;
                  }
                  return `${option.EntityCode} - ${option.EntityName}`;
                }}
                isOptionEqualToValue={(option, value) => {
                  const optionCode = typeof option === 'object' ? option.EntityCode : option;
                  const valueCode = typeof value === 'object' ? value.EntityCode : value;
                  return optionCode === valueCode;
                }}
                loading={entityLoading}
                value={currentState.EntityCode || ''}
                onChange={(e, newValue) => {
                  const newEntityCode = typeof newValue === 'object' ? newValue?.EntityCode || '' : newValue || '';
                  const oldEntityCode = currentState.EntityCode;
                  const newFilingCode = generateFilingCode({
                    ...currentState,
                    EntityCode: newEntityCode
                  });

                  // Remove old filing entity from entityList if it was there
                  let updatedEntityList = (currentState.entityList || []).filter((item) => {
                    const code = typeof item === 'object' ? item.EntityCode : item;
                    return code !== oldEntityCode;
                  });

                  // Add new filing entity to entityList if not already present
                  if (newEntityCode && !updatedEntityList.some((item) => {
                    const code = typeof item === 'object' ? item.EntityCode : item;
                    return code === newEntityCode;
                  })) {
                    const entity = masterEntities.find((ent) => ent.EntityCode === newEntityCode);
                    if (entity) {
                      updatedEntityList = [{
                        EntityCode: entity.EntityCode,
                        EntityName: entity.EntityName,
                        PointInTime: entity.PointInTime
                      }, ...updatedEntityList];
                    }
                  }

                  setCurrentState({
                    ...currentState,
                    EntityCode: newEntityCode,
                    EntityName: masterEntities.find((ent) => ent.EntityCode === newEntityCode)?.EntityName || '',
                    FilingCode: newFilingCode,
                    entityList: updatedEntityList
                  });
                }}
                renderInput={(params) =>
                <TextField {...params} margin="dense" label="Filing Entity Code" fullWidth required autoComplete="off" />
                }
                noOptionsText="No entities available"
                disabled={editMode} />

            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Filing Type</InputLabel>
                <Select
                  value={currentState.FilingType || ''}
                  label="Filing Type"
                  onChange={(e) => {
                    const newFilingType = e.target.value;
                    const newFilingCode = generateFilingCode({
                      ...currentState,
                      FilingType: newFilingType
                    });
                    setCurrentState({
                      ...currentState,
                      FilingType: newFilingType,
                      FilingCode: newFilingCode
                    });
                  }}
                  disabled={editMode}>

                  <MenuItem value="Separate">Separate</MenuItem>
                  <MenuItem value="Unitary/Combined">Unitary/Combined</MenuItem>
                  <MenuItem value="Nexus">Nexus</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel>Tax Type</InputLabel>
                <Select
                  value={currentState.TaxType || ''}
                  label="Tax Type"
                  onChange={(e) => setCurrentState({ ...currentState, TaxType: e.target.value })}>

                  <MenuItem value="Income">Income</MenuItem>
                  <MenuItem value="Franchise">Franchise</MenuItem>
                  <MenuItem value="Minimum">Minimum</MenuItem>
                  <MenuItem value="Other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Filing Name"
                value={currentState.FilingName || ''}
                onChange={(e) => setCurrentState({ ...currentState, FilingName: e.target.value })}
                autoComplete="off" />

            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={US_STATES}
                getOptionLabel={(option) => {
                  // If option is a string (the state code), find the full state object
                  if (typeof option === 'string') {
                    const state = US_STATES.find((s) => s.code === option);
                    return state ? `${state.code} - ${state.name}` : option;
                  }
                  // If option is the full state object
                  return `${option.code} - ${option.name}`;
                }}
                value={currentState.State ? US_STATES.find((s) => s.code === currentState.State) || currentState.State : null}
                onChange={(e, newValue) => {
                  const newStateCode = newValue ? newValue.code : '';
                  const newFilingCode = generateFilingCode({
                    ...currentState,
                    State: newStateCode
                  });
                  setCurrentState({
                    ...currentState,
                    State: newStateCode,
                    FilingCode: newFilingCode
                  });
                }}
                renderInput={(params) =>
                <TextField
                  {...params}
                  margin="dense"
                  label="State"
                  fullWidth
                  required
                  autoComplete="off" />

                }
                disabled={editMode}
                isOptionEqualToValue={(option, value) => {
                  if (typeof value === 'string') {
                    return option.code === value;
                  }
                  return option.code === value?.code;
                }} />

            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                margin="dense"
                label="Filing Code"
                value={currentState.FilingCode || ''}
                InputProps={{ readOnly: true }}
                autoComplete="off" />

            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                margin="dense"
                label="Point In Time"
                value={currentState.PointInTimeCode || currentState.PointInTime || ''}
                onChange={(e) => {
                  const newPIT = e.target.value; // code like '2025-Q1'
                  // If current entity doesn't belong to the new PIT, clear it
                  const entityValidForPIT = masterEntities.some((ent) => ent.EntityCode === currentState.EntityCode && ent.PointInTime === newPIT);
                  const pitObj = pointInTimes.find((p) => p.Code === newPIT);
                  const nextEntityCode = entityValidForPIT ? currentState.EntityCode : '';
                  const newFilingCode = generateFilingCode({ ...currentState, EntityCode: nextEntityCode });
                  setCurrentState({
                    ...currentState,
                    // Persist code in both fields expected elsewhere in the app
                    PointInTime: newPIT,
                    PointInTimeCode: newPIT,
                    EntityCode: nextEntityCode,
                    FilingCode: newFilingCode
                  });
                }}
                required>

                {pointInTimes.map((pit) =>
                <MenuItem key={pit.id} value={pit.Code}>
                    {pit.Code} - {pit.Name}
                  </MenuItem>
                )}
              </TextField>
            </Grid>
          </Grid>
          
          {/* Associated Entities Section */}
          {currentState.PointInTime &&
          <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Associated Entities
              </Typography>
              
              {/* Show current entities as chips */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {currentState.entityList?.map((entity) => {
                // Handle both object format and string format for backward compatibility
                const entityCode = typeof entity === 'object' ? entity.EntityCode : entity;
                const entityName = typeof entity === 'object' ? entity.EntityName : entities.find((e) => e.EntityCode === entity)?.EntityName || entity;

                const isFilingEntity = entityCode === currentState.EntityCode;

                return (
                  <Paper key={entityCode} sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5 }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {entityCode} - {entityName}
                      </Typography>
                      {!isFilingEntity &&
                    <IconButton size="small" color="error" onClick={() => {
                      const newEntityList = currentState.entityList.filter((item) => {
                        const itemCode = typeof item === 'object' ? item.EntityCode : item;
                        return itemCode !== entityCode;
                      });
                      setCurrentState({ ...currentState, entityList: newEntityList });
                    }}>
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                    }
                    </Paper>);

              })}
              </Box>
              
              {/* Add new entity */}
              <Grid container spacing={1} alignItems="center">
                <Grid item>
                  <Autocomplete
                  size="small"
                  options={(() => {
                    // Get the state's PointInTime code (could be in PointInTime or PointInTimeCode field)
                    const statePointInTime = currentState.PointInTimeCode || currentState.PointInTime;



                    return entities.filter((entity) => {
                      const entityCodes = currentState.entityList?.map((item) =>
                      typeof item === 'object' ? item.EntityCode : item
                      ) || [];

                      // Only show entities that match the state's PointInTime and are not already selected
                      const matchesPointInTime = entity.PointInTime === statePointInTime;
                      const notAlreadySelected = !entityCodes.includes(entity.EntityCode);



                      return matchesPointInTime && notAlreadySelected;
                    }).filter((e, index, self) => index === self.findIndex((x) => x.EntityCode === e.EntityCode)).
                    sort((a, b) => (a.EntityCode || '').localeCompare(b.EntityCode || ''));
                  })()}
                  getOptionLabel={(entity) => {
                    return `${entity.EntityCode} - ${entity.EntityName}`;
                  }}
                  value={selectedEntity}
                  onChange={(event, newValue) => {
                    setSelectedEntity(newValue);
                  }}
                  renderInput={(params) =>
                  <TextField
                    {...params}
                    label="Select Entity"
                    size="small"
                    variant="outlined"
                    sx={{
                      width: '300px',
                      '& .MuiOutlinedInput-root': {
                        minWidth: '300px'
                      }
                    }} />

                  }
                  loading={loadingEntities}
                  disabled={loadingEntities} />

                </Grid>
                <Grid item>
                  <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    if (!selectedEntity) return;

                    const entityCode = selectedEntity.EntityCode;
                    const entityCodes = currentState.entityList?.map((item) =>
                    typeof item === 'object' ? item.EntityCode : item
                    ) || [];

                    if (entityCodes.includes(entityCode)) {
                      return;
                    }

                    // Update entity list - add the full entity object for consistency
                    const newEntityList = [...(currentState.entityList || []), {
                      EntityCode: selectedEntity.EntityCode,
                      EntityName: selectedEntity.EntityName,
                      PointInTime: selectedEntity.PointInTime
                    }];

                    setCurrentState({
                      ...currentState,
                      entityList: newEntityList
                    });

                    setSelectedEntity(null);
                  }}
                  disabled={!selectedEntity}>

                    Add
                  </Button>
                </Grid>
              </Grid>
            </Box>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" color="primary">{editMode ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

    </Box>);

};

export default StateManagement;
